import { Injectable, signal, computed } from '@angular/core';
import { UnitSystem, UnitQuantity, UNIT_DEFINITIONS } from '../models/unit.model';

export type DensityVariant = 'kg/m³' | 'ton/mm³';
export type MassVariant = 'kg' | 'g';

const KG_TO_G = 1000;

// "ton" here is the metric tonne (1000 kg) used by the FEA mm-tonne-s consistent unit set.
// 1 tonne/mm³ = 1000 kg / 10^-9 m³ = 1e12 kg/m³, so kg/m³ → tonne/mm³ multiplies by 1e-12.
// (Steel ≈ 7800 kg/m³ = 7.8e-9 tonne/mm³, matching Abaqus/Ansys conventions.)
const KG_M3_TO_TON_MM3 = 1e-12;

/**
 * Snap a float to the shortest decimal representation that is within ULP
 * of the value. Eliminates floating-point round-trip noise so the user
 * sees clean values (e.g. typing `9` lbf renders as `9`, not `8.999…998`).
 *
 * Imperial conversions multiply or divide by an irrational-in-binary factor
 * (e.g. 1 lbf = 4.4482216… N), so the round-trip `value → SI → value` may
 * leave a residual at any digit position scaled to the value's magnitude.
 * `toPrecision(12)` is not enough — the noise can land in the 12th sig fig.
 *
 * Strategy: try increasing decimal precision (0, 1, 2, … decimals) and pick
 * the first candidate within tolerance. Tolerance is `1e-10` relative (well
 * above float64 ULP ≈ 2e-16, well below any meaningful user precision).
 *
 * Applied at both `parse` (display→SI) and `convert` (SI→display) boundaries.
 */
function suppressFloatNoise(value: number): number {
  if (!Number.isFinite(value) || value === 0) return value;
  const tolerance = Math.max(Math.abs(value) * 1e-10, 1e-15);
  for (let d = 0; d <= 10; d++) {
    const factor = Math.pow(10, d);
    const candidate = Math.round(value * factor) / factor;
    if (Math.abs(value - candidate) <= tolerance) {
      return candidate;
    }
  }
  return value;
}

@Injectable({
  providedIn: 'root'
})
export class UnitConversionService {
  readonly unitSystem = signal<UnitSystem>('metric');
  readonly isImperial = computed(() => this.unitSystem() === 'imperial');
  readonly densityVariant = signal<DensityVariant>('kg/m³');
  readonly massVariant = signal<MassVariant>('kg');

  setUnitSystem(system: UnitSystem): void {
    this.unitSystem.set(system);
  }

  setDensityVariant(variant: DensityVariant): void {
    this.densityVariant.set(variant);
  }

  setMassVariant(variant: MassVariant): void {
    this.massVariant.set(variant);
  }

  convert(value: number, quantity: UnitQuantity, system: UnitSystem): number {
    if (quantity === 'density' && system === 'metric') {
      return this.densityVariant() === 'ton/mm³'
        ? suppressFloatNoise(value * KG_M3_TO_TON_MM3)
        : value;
    }
    if (quantity === 'mass' && system === 'metric') {
      return this.massVariant() === 'g'
        ? suppressFloatNoise(value * KG_TO_G)
        : value;
    }
    if (system === 'metric') return value;
    const def = UNIT_DEFINITIONS[quantity];
    return suppressFloatNoise(value * def.factor);
  }

  format(value: number, quantity: UnitQuantity, system: UnitSystem): string {
    const converted = this.convert(value, quantity, system);
    const def = UNIT_DEFINITIONS[quantity];
    const label = this.getUnitLabel(quantity, system);
    return `${this.formatNumber(converted, def.precision)} ${label}`;
  }

  parse(displayValue: number, quantity: UnitQuantity, system: UnitSystem): number {
    if (quantity === 'density' && system === 'metric') {
      return this.densityVariant() === 'ton/mm³'
        ? suppressFloatNoise(displayValue / KG_M3_TO_TON_MM3)
        : displayValue;
    }
    if (quantity === 'mass' && system === 'metric') {
      return this.massVariant() === 'g'
        ? suppressFloatNoise(displayValue / KG_TO_G)
        : displayValue;
    }
    if (system === 'metric') return displayValue;
    const def = UNIT_DEFINITIONS[quantity];
    return suppressFloatNoise(displayValue / def.factor);
  }

  getUnitLabel(quantity: UnitQuantity, system: UnitSystem): string {
    if (quantity === 'density' && system === 'metric') {
      return this.densityVariant();
    }
    if (quantity === 'mass' && system === 'metric') {
      return this.massVariant();
    }
    const def = UNIT_DEFINITIONS[quantity];
    return system === 'metric' ? def.siUnit : def.impUnit;
  }

  private formatNumber(value: number, precision: number): string {
    // A nonzero value must never render as "0.00": when it would round to
    // zero at the quantity's precision, extend to the first two significant
    // digits (capped) so e.g. 0.004 kg shows as "0.004" instead of "0.00".
    let maximumFractionDigits = precision;
    if (value !== 0 && Math.abs(value) < Math.pow(10, -precision) / 2) {
      const firstSignificantDigit = Math.ceil(-Math.log10(Math.abs(value)));
      maximumFractionDigits = Math.min(firstSignificantDigit + 1, 6);
    }
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: precision,
      maximumFractionDigits: Math.max(maximumFractionDigits, precision),
    }).format(value);
  }
}
